import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { Modal } from '@/components/Modal'
import { SortHeader } from '@/components/ui/sort-header'
import { useSortable } from '@/lib/useSortable'
import { MONO, fmt, monthLabel } from './format'
import { SegmentedToggle } from './SegmentedToggle'
import type { CreditNoteAxis, CreditNoteDetail, CreditNotes, CreditNotesByHierarchy } from '../types'

// "Jan-26" → a chronological ordinal so month columns sort by date, not alphabet.
const M3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthOrd = (m: string): number => {
  const [mon, yy] = m.split('-')
  const mi = M3.indexOf(mon)
  return mi < 0 ? 0 : Number(`20${yy}`) * 12 + mi
}
const cnValue = (d: CreditNoteDetail, key: string): number | string => {
  switch (key) {
    case 'clinic': return d.clinic
    case 'type': return d.type
    case 'amount': return d.amount
    case 'orderMonth': return monthOrd(d.orderMonth)
    case 'raisedMonth': return monthOrd(d.raisedMonth)
    default: return 0
  }
}

interface CreditNotesSectionProps {
  creditNotes: CreditNotes
  currency: string
  /** The YTD year the headline totals cover (the bars below span all 17 months). */
  ytdYear: number
}

const SLATE = '#5E6E8C'
const AXES: readonly CreditNoteAxis[] = ['Order month', 'Invoice month']

/** Collapsed Credit Notes section: reversal (deducted) vs PC memo, two axes.
 *  Clicking a month bar drills into that month's credit notes by business unit (the
 *  accountant's lines — "PC & Lab", "Education" — sit at unit level). */
export function CreditNotesSection({ creditNotes: cn0, currency, ytdYear }: CreditNotesSectionProps) {
  const [open, setOpen] = useState(false)
  const [axis, setAxis] = useState<CreditNoteAxis>('Invoice month')
  const [drillMonth, setDrillMonth] = useState<string | null>(null)
  const inv = axis === 'Invoice month'

  const drill = drillMonth ? cn0.byMonth.find((m) => m.month === drillMonth) : null
  const drillCentres = (drill?.centres ?? [])
    .map((c) => ({ ...c, amount: inv ? c.inv : c.ord }))
    .filter((c) => c.amount > 0)
    .sort((a, b) => b.amount - a.amount)
  const drillReversals = drillCentres.filter((c) => c.group === 'reversal')
  const drillMemos = drillCentres.filter((c) => c.group === 'memo')

  const reversalYtd = inv ? cn0.reversalInvYtd : cn0.reversalOrdYtd
  const memoYtd = inv ? cn0.memoInvYtd : cn0.memoOrdYtd
  const bars = cn0.byMonth.map((m) => ({
    month: m.month,
    reversal: inv ? m.invReversal : m.ordReversal,
    memo: inv ? m.invMemo : m.ordMemo,
  }))
  const maxBar = Math.max(1, ...bars.map((b) => b.reversal + b.memo))
  const { sorted: detailRows, sortKey, sortDir, toggle } = useSortable(cn0.detail, cnValue, { key: 'amount', dir: 'desc' })

  return (
    <div className="rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Credit Notes</h3>
          <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600 dark:bg-red-500/15 dark:text-red-300">
            Other reversals (deducted) · YTD {ytdYear} −{fmt(reversalYtd, currency)}
          </span>
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-500 dark:bg-stone-800 dark:text-stone-400">
            PC credit (memo) · YTD {ytdYear} −{fmt(memoYtd, currency)}
          </span>
        </div>
        <ChevronDown className={cn('h-5 w-5 shrink-0 text-stone-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="border-t border-stone-100 p-5 dark:border-stone-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-md text-xs text-stone-400 dark:text-stone-500">
              A credit note voids an invoice; the two axes date it differently (original order month vs the month raised).
              PC-credit memos are shown for reference and never reduce sales.
            </p>
            <SegmentedToggle options={AXES} value={axis} onChange={setAxis} size="sm" ariaLabel="Credit note axis" />
          </div>

          <div className="mt-4 flex items-end gap-2">
            {bars.map((b) => {
              const hasAny = b.reversal + b.memo > 0
              return (
                <button
                  key={b.month}
                  type="button"
                  onClick={() => hasAny && setDrillMonth(b.month)}
                  aria-label={`Credit notes for ${monthLabel(b.month)}`}
                  className={cn(
                    'flex flex-1 flex-col items-center gap-1 rounded-md px-0.5 pt-1 transition-colors',
                    hasAny ? 'cursor-pointer hover:bg-champagne-100/60 dark:hover:bg-champagne-500/10' : 'cursor-default',
                  )}
                >
                  <div className="flex h-24 w-full max-w-[44px] flex-col justify-end">
                    <div
                      className="w-full rounded-t bg-champagne-300/70"
                      style={{ height: `${(b.memo / maxBar) * 100}%` }}
                      title={`PC memo ${fmt(b.memo, currency)}`}
                    />
                    <div className="w-full" style={{ height: `${(b.reversal / maxBar) * 100}%`, backgroundColor: SLATE }} title={`Reversal ${fmt(b.reversal, currency)}`} />
                  </div>
                  <span className="text-[10px] text-stone-400">{b.month}</span>
                </button>
              )
            })}
          </div>
          <p className="mt-1.5 text-center text-[11px] text-stone-400 dark:text-stone-500">
            Bars cover all {cn0.byMonth.length} months · totals above are YTD {ytdYear} · click a month for its breakdown
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px] text-stone-500 dark:text-stone-400">
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SLATE }} />
              Other reversals (deducted) · YTD {ytdYear} −{fmt(reversalYtd, currency)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-champagne-300" />
              PC credit memo · YTD {ytdYear} −{fmt(memoYtd, currency)}
            </span>
          </div>

          {cn0.detail.length ? (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="text-xs text-stone-400">
                    <th className="pb-2 text-left font-medium">
                      <SortHeader label="Clinic" sortKey="clinic" active={sortKey === 'clinic'} dir={sortDir} onSort={toggle} align="left" />
                    </th>
                    <th className="pb-2 text-left font-medium">
                      <SortHeader label="Type" sortKey="type" active={sortKey === 'type'} dir={sortDir} onSort={toggle} align="left" />
                    </th>
                    <th className="pb-2 text-right font-medium">
                      <SortHeader label="Amount" sortKey="amount" active={sortKey === 'amount'} dir={sortDir} onSort={toggle} />
                    </th>
                    <th className="pb-2 text-right font-medium">
                      <SortHeader label="Order month" sortKey="orderMonth" active={sortKey === 'orderMonth'} dir={sortDir} onSort={toggle} />
                    </th>
                    <th className="pb-2 text-right font-medium">
                      <SortHeader label="Raised" sortKey="raisedMonth" active={sortKey === 'raisedMonth'} dir={sortDir} onSort={toggle} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((d) => (
                    <tr key={d.id} className="border-t border-stone-50 dark:border-stone-800/60">
                      <td className="py-2 pr-3 text-stone-700 dark:text-stone-200">{d.clinic}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={cn(
                            'whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium',
                            d.type === 'PC credit memo'
                              ? 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
                              : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
                          )}
                        >
                          {d.type}
                        </span>
                      </td>
                      <td className={cn('py-2 text-right', MONO)}>−{fmt(d.amount, currency)}</td>
                      <td className="py-2 text-right text-stone-400">{d.orderMonth}</td>
                      <td className="py-2 text-right text-stone-400">{d.raisedMonth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-5 rounded-lg bg-stone-50 px-3 py-4 text-center text-xs text-stone-400 dark:bg-stone-800/50">
              No itemised credit notes for {currency} this period.
            </p>
          )}
        </div>
      )}

      <Modal
        open={!!drillMonth}
        onClose={() => setDrillMonth(null)}
        eyebrow={`Credit notes · ${axis}`}
        title={drillMonth ? monthLabel(drillMonth) : ''}
        subtitle="By business unit"
        footer="This is the breakdown the export carries today. Individual clinic-level credit notes will appear here once the accountant feed is included in the monthly export."
      >
        {drillCentres.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                <th className="pb-2 pr-3 text-left font-medium">Business unit · line</th>
                <th className="pb-2 pl-3 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {drillReversals.map((c) => (
                <tr key={c.centre} className="border-b border-stone-50 dark:border-stone-800/60">
                  <td className="py-2.5 pr-3 text-stone-700 dark:text-stone-200">{c.centre}</td>
                  <td className={cn('py-2.5 pl-3 text-right text-red-600 dark:text-red-400', MONO)}>−{fmt(c.amount, currency)}</td>
                </tr>
              ))}
              {drillMemos.map((c) => (
                <tr key={c.centre} className="border-b border-stone-50 dark:border-stone-800/60">
                  <td className="py-2.5 pr-3 text-stone-500 dark:text-stone-400">
                    {c.centre}
                    <span className="ml-1.5 rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-500 dark:bg-stone-800 dark:text-stone-400">memo</span>
                  </td>
                  <td className={cn('py-2.5 pl-3 text-right text-stone-500 dark:text-stone-400', MONO)}>−{fmt(c.amount, currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                <td className="pt-3 font-semibold">Deducted from sales</td>
                <td className={cn('pt-3 pl-3 text-right font-bold text-red-600 dark:text-red-400', MONO)}>
                  −{fmt(drillReversals.reduce((t, c) => t + c.amount, 0), currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p className="rounded-lg bg-stone-50 px-3 py-4 text-center text-xs text-stone-400 dark:bg-stone-800/50">
            No credit notes recorded for {drillMonth ? monthLabel(drillMonth) : 'this month'} on the {axis.toLowerCase()} axis.
          </p>
        )}
      </Modal>
    </div>
  )
}

interface CreditNotesByDepartmentProps {
  /** creditNotes.byHierarchy of the current view (EUR, USD or the merged Consolidated tree). */
  tree: CreditNotesByHierarchy | undefined
  currency: string
  ytdYear: number
  /** The YTD months, to list the unallocated lines that fall inside the YTD. */
  monthsInYtd: string[]
}

/** Who gets the credit notes: every deducted reversal traced to its business unit ›
 *  department, ranked by value with its share of the total. Totals tie to the revenue
 *  table's Credit notes row; PC-credit memos are listed apart and greyed (never deducted). */
export function CreditNotesByDepartment({ tree, currency, ytdYear, monthsInYtd }: CreditNotesByDepartmentProps) {
  const [openUnalloc, setOpenUnalloc] = useState<string | null>(null)
  const heading = (
    <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
      Credit notes by business unit › department · YTD {ytdYear}
      <InfoPopover term="creditNotesByDepartment" />
    </h3>
  )
  if (!tree) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
        {heading}
        <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
          This data bundle carries credit notes by business unit only. Upload a refresh built with the current export to see them by department.
        </p>
      </div>
    )
  }
  const rows = tree.units.flatMap((u) =>
    u.departments.map((d) => ({ key: `${u.id}:${d.id}`, unitId: u.id, unit: u.name, deptId: d.id, department: d.name, reversal: d.reversalYtd, memo: d.memoYtd })),
  )
  const total = tree.units.reduce((t, u) => t + u.reversalYtd, 0)
  const memoTotal = tree.units.reduce((t, u) => t + u.memoYtd, 0)
  const ranked = rows.filter((r) => r.reversal > 0.005).sort((a, b) => b.reversal - a.reversal)
  const memos = rows.filter((r) => r.memo > 0.005).sort((a, b) => b.memo - a.memo)
  const unallocLines = (unitId: string, group: 'reversal' | 'memo') =>
    tree.unallocated.filter((l) => l.unit === unitId && l.group === group && monthsInYtd.includes(l.month)).sort((a, b) => b.amount - a.amount)
  const pct = (v: number, of: number) => (of ? (v / of) * 100 : 0)

  /** "Unallocated ⓘ · 3 credit notes ▸" — the toggle lists the lines behind the figure. */
  const unallocLabel = (key: string, unitId: string, unitName: string, label: string, group: 'reversal' | 'memo') => {
    const n = unallocLines(unitId, group).length
    return (
      <span className="inline-flex flex-wrap items-center gap-x-1.5">
        {label}
        <InfoPopover term="creditNotesUnallocated" />
        {n > 0 && (
          <button
            type="button"
            onClick={() => setOpenUnalloc((k) => (k === key ? null : key))}
            aria-expanded={openUnalloc === key}
            aria-label={`${openUnalloc === key ? 'Hide' : 'Show'} the ${unitName} unallocated ${group === 'memo' ? 'PC-credit memos' : 'credit notes'}`}
            className="inline-flex items-center gap-0.5 text-xs text-stone-400 hover:text-stone-700 dark:text-stone-500 dark:hover:text-stone-200"
          >
            {n} {n === 1 ? 'credit note' : 'credit notes'}
            <ChevronRight className={cn('h-3 w-3 shrink-0 transition-transform', openUnalloc === key && 'rotate-90')} />
          </button>
        )}
      </span>
    )
  }
  const unallocList = (unitId: string, group: 'reversal' | 'memo') => (
    <ul className="mt-1.5 space-y-1 border-l border-stone-100 pl-3 text-xs text-stone-500 dark:border-stone-800 dark:text-stone-400">
      {unallocLines(unitId, group).map((l, i) => (
        <li key={`${l.creditNote}-${i}`} className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="min-w-0">
            <span className="text-stone-400 dark:text-stone-500">{l.month} · {l.creditNote}</span> {l.product ?? 'No product traced'}
            {l.productLabel ? <span className="text-stone-400 dark:text-stone-500"> — belongs to {l.productLabel}</span> : null}
          </span>
          <span className={cn('shrink-0', group === 'reversal' ? 'text-red-600 dark:text-red-400' : '', MONO)}>−{fmt(l.amount, currency)}</span>
        </li>
      ))}
    </ul>
  )

  return (
    <div data-testid="credit-notes-by-department" className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          {heading}
          <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            Deducted reversals on the month each was raised, ranked · the business units add up to the revenue table&apos;s Credit notes row
          </p>
        </div>
        <span className={cn('text-sm font-semibold text-red-600 dark:text-red-400', MONO)} data-cn-total data-value={total}>
          −{fmt(total, currency)}
        </span>
      </div>

      {ranked.length ? (
        <ol className="mt-4 divide-y divide-stone-50 dark:divide-stone-800/60">
          {ranked.map((r) => {
            const share = pct(r.reversal, total)
            const label = `${r.unit} › ${r.department}`
            return (
              <li key={r.key} data-cn-rank={r.key} data-value={r.reversal} className="py-2">
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 text-sm text-stone-700 dark:text-stone-200">
                    <span className="text-stone-400 dark:text-stone-500">{r.unit} › </span>
                    {r.deptId === 'unallocated' ? unallocLabel(r.key, r.unitId, r.unit, r.department, 'reversal') : r.department}
                  </span>
                  <div className="hidden h-1.5 w-40 overflow-hidden rounded-full bg-stone-100 sm:block dark:bg-stone-800" aria-hidden>
                    <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: SLATE }} />
                  </div>
                  <span className={cn('w-24 shrink-0 text-right text-sm font-medium text-red-600 dark:text-red-400', MONO)} title={label}>
                    −{fmt(r.reversal, currency)}
                  </span>
                  <span className={cn('w-12 shrink-0 text-right text-xs text-stone-400 dark:text-stone-500', MONO)}>{share.toFixed(1)}%</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-100 sm:hidden dark:bg-stone-800" aria-hidden>
                  <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: SLATE }} />
                </div>
                {r.deptId === 'unallocated' && openUnalloc === r.key && unallocList(r.unitId, 'reversal')}
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="mt-4 rounded-lg bg-stone-50 px-3 py-4 text-center text-xs text-stone-400 dark:bg-stone-800/50">No deducted credit notes in YTD {ytdYear}.</p>
      )}

      {memos.length > 0 && (
        <div className="mt-4 border-t border-stone-100 pt-3 dark:border-stone-800">
          <p className="flex flex-wrap items-baseline justify-between gap-2 text-xs font-medium text-stone-500 dark:text-stone-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-champagne-300" aria-hidden />
              PC credit (memo) · not deducted
            </span>
            <span className={MONO}>−{fmt(memoTotal, currency)}</span>
          </p>
          <ul className="mt-1.5 space-y-1">
            {memos.map((r) => (
              <li key={r.key} data-cn-memo={r.key} className="text-xs text-stone-400 dark:text-stone-500">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    {r.unit} ›{' '}
                    {r.deptId === 'unallocated' ? unallocLabel(`memo:${r.key}`, r.unitId, r.unit, r.department, 'memo') : r.department}
                  </span>
                  <span className={MONO}>−{fmt(r.memo, currency)}</span>
                </div>
                {r.deptId === 'unallocated' && openUnalloc === `memo:${r.key}` && unallocList(r.unitId, 'memo')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
